import React, { ReactNode, useMemo } from "react";
import { client as GQLClient } from "./graphQLClient";
import { Provider } from "urql";
import { useAuth0 } from "@auth0/auth0-react";
import { useEnv } from "store/env";
import { useNotification } from "collections/Notifications";
import { useGraphqlActions } from "store";
import { datadogLogs } from "@datadog/browser-logs";
import { LoggerOptions } from "../monitoring/LoggerOptions";
import { useLogout } from "../auth";
import { WithOptimizelyProps } from "@optimizely/react-sdk/dist/withOptimizely";
import { withOptimizely } from "@optimizely/react-sdk";

const logger = datadogLogs.createLogger("graphql", LoggerOptions);

/**
 * Create the provider for out graph client.
 * Depends on authentication and redux.
 * @param children
 * @constructor
 */
const UrqlProvider: React.FC<{ children: any } & WithOptimizelyProps> = ({
  children,
  optimizely
}) => {
  const { getAccessTokenSilently, isLoading, getIdTokenClaims } = useAuth0();
  const logout = useLogout();
  const { graphqlEndpoint, verboseUrqlLogs } = useEnv();
  const [addNotification] = useNotification();
  const graphqlActions = useGraphqlActions();
  const client = useMemo(() => {
    if (isLoading) {
      return null;
    }
    return GQLClient({
      url: graphqlEndpoint,
      auth0: {
        getAccessTokenSilently,
        getIdTokenClaims,
        logout
      },
      optimizely,
      notify: addNotification,
      logger,
      verboseUrqlLogs,
      dispatcher: graphqlActions
    });
  }, [
    graphqlEndpoint,
    isLoading,
    addNotification,
    verboseUrqlLogs,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
    graphqlActions,
    optimizely
  ]);
  if (isLoading || !client) {
    return null;
  }
  return <Provider value={client}>{children}</Provider>;
};
const wrapped: React.FC<{ children: ReactNode }> = withOptimizely(UrqlProvider);
export default wrapped;
